import React from 'react';
import { Box, Typography, Button, Container, Grid, Stack } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import { motion } from 'framer-motion';

// Swiper modules
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';

// Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const HeroSection = () => {
  const primarySaffron = "#FF8C00"; 
  const deepGolden = "#B8860B"; 
  const accentKesari = "#DD2476"; 

  const slides = [
    {
      title: "स्वर-भक्ती संग्रह",
      subtitle: "जिनेद्र भक्तीची अमृतधारा",
      description: "निवडक आणि सुमधुर भक्ती गीतांच्या संग्रहातून अनुभवा आध्यात्मिक आनंद. मनाला प्रसन्न करणारी भक्तीगीते आता एकाच ठिकाणी.",
      buttonText: "संगीत ऐका",
      iconBtn: <LibraryMusicIcon />,
      background: `linear-gradient(135deg, ${primarySaffron} 0%, ${accentKesari} 100%)`
    },
    {
      title: "स्वरांजली कराओके",
      subtitle: "तुमच्या भक्तीला द्या स्वतःचा स्वर",
      description: "उच्च दर्जाच्या म्युझिक ट्रॅकसह स्वतः भक्तीगीते गाण्यासाठी आमचा खास 'बिनवाणी' (Karaoke) संग्रह नक्की पहा.",
      buttonText: "ट्रॅक पहा",
      iconBtn: <PlayCircleOutlineIcon />,
      background: `linear-gradient(135deg, #FFD700 0%, ${deepGolden} 100%)`
    }
  ];

  return (
    <Box sx={{ 
      width: '100%', 
      position: 'relative', 
      overflow: 'hidden',
      height: { xs: '65vh', md: '60vh' },
      minHeight: { xs: '480px', md: '550px' } 
    }}>
      
      {/* --- CORRECTLY ORIENTED & ANIMATED OM LOGO --- */}
      <Box 
        component={motion.div}
        animate={{ 
          scale: [1, 1.05, 1],
          opacity: [0.15, 0.3, 0.15],
        }}
        transition={{ 
          duration: 6, 
          repeat: Infinity, 
          ease: "easeInOut" 
        }}
        sx={{
          position: 'absolute',
          right: { xs: '2%', md: '8%' },
          top: { xs: '15%', md: '5%' },
          fontSize: { xs: '180px', md: '320px' },
          color: '#FFFFFF',
          fontWeight: 400, // Regular weight for clean look
          zIndex: 1, // मजकुराच्या मागे
          pointerEvents: 'none',
          // 'sans-serif' किंवा 'serif' वापरून सिम्बॉल सरळ राहील याची खात्री
          fontFamily: "'Noto Sans Marathi', sans-serif", 
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Glow effect
          filter: 'blur(1px) drop-shadow(0px 0px 20px rgba(255,255,255,0.3))',
          userSelect: 'none',
          transformOrigin: 'center center'
        }}
      >
        ॐ
      </Box>

      <Swiper
        modules={[Autoplay, Pagination, EffectFade]}
        effect={'fade'}
        speed={1000}
        loop={true}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        style={{ width: '100%', height: '100%' }}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index} style={{ background: slide.background }}>
            <Container maxWidth="lg" sx={{ height: '100%', display: 'flex', alignItems: 'center', position: 'relative', zIndex: 3 }}>
              <Grid container>
                <Grid item xs={12} md={8}>
                  <Stack 
                    spacing={{ xs: 2.5, md: 3 }} 
                    sx={{ 
                      alignItems: { xs: 'center', md: 'flex-start' }, 
                      textAlign: { xs: 'center', md: 'left' },
                      px: { xs: 2, md: 0 }
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, x: -40 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8 }}
                    >
                      <Typography 
                        variant="h1" 
                        sx={{ 
                          fontWeight: 900, 
                          color: '#fff',
                          fontSize: { xs: '2.4rem', sm: '3rem', md: '3.8rem' },
                          lineHeight: 1.1,
                          textShadow: '0px 4px 15px rgba(0,0,0,0.2)'
                        }}
                      >
                        {slide.title}
                      </Typography>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: 500, 
                          color: '#FFEBB7', 
                          fontSize: { xs: '1.2rem', md: '1.8rem' },
                          letterSpacing: 0.5
                        }}
                      >
                        {slide.subtitle}
                      </Typography>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 1, delay: 0.4 }}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'rgba(255,255,255,0.95)', 
                          fontSize: { xs: '0.95rem', md: '1.1rem' },
                          maxWidth: '550px',
                          lineHeight: 1.8,
                          display: { xs: 'none', sm: 'block' }
                        }}
                      >
                        {slide.description}
                      </Typography>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.6 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button 
                        variant="contained" 
                        startIcon={slide.iconBtn}
                        sx={{ 
                          bgcolor: '#fff', 
                          color: deepGolden, 
                          fontWeight: 900, 
                          px: { xs: 5, md: 6 },
                          py: { xs: 1.5, md: 1.8 },
                          borderRadius: '50px',
                          fontSize: { xs: '1rem', md: '1.1rem' },
                          textTransform: 'none',
                          boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                          '&:hover': { bgcolor: '#FFF9E1' },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        {slide.buttonText}
                      </Button>
                    </motion.div>
                  </Stack>
                </Grid>
              </Grid>
            </Container>
          </SwiperSlide>
        ))}
      </Swiper>

      <style>
        {`
          .swiper-pagination-bullet {
            background: #fff !important;
            opacity: 0.4;
            height: 8px;
            width: 8px;
          }
          .swiper-pagination-bullet-active {
            opacity: 1;
            width: 30px !important;
            border-radius: 6px !important;
            background: #fff !important;
            transition: width 0.3s ease-in-out !important;
          }
        `}
      </style>
    </Box>
  );
};

export default HeroSection;